import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';

interface EmojiPopoverProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLElement>;
  onEmojiSelect: (emoji: any) => void;
  onClose: () => void;
}

const PICKER_W = 352;
const PICKER_H = 435;

/**
 * Bảng chọn emoji dùng chung — render qua PORTAL ra document.body với position: fixed nên KHÔNG bị
 * cha (dialog/khung bài viết) có overflow-hidden cắt mất (lỗi tester: emoji bị cắt ở trên / vỡ UI).
 * Tự lật lên/xuống theo chỗ trống quanh nút bấm + kẹp trong màn hình. Đóng khi click ra ngoài / cuộn.
 */
const EmojiPopover = ({ open, anchorRef, onEmojiSelect, onClose }: EmojiPopoverProps) => {
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Ưu tiên hiện PHÍA TRÊN nút; không đủ chỗ thì hiện phía dưới.
    const above = r.top >= PICKER_H + 12;
    const top = above ? r.top - PICKER_H - 8 : Math.min(r.bottom + 8, vh - PICKER_H - 8);
    // Canh mép phải nút, kẹp trong màn hình.
    let left = r.right - PICKER_W;
    left = Math.max(8, Math.min(left, vw - PICKER_W - 8));
    setPos({ top: Math.max(8, top), left });
  }, [open, anchorRef]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t)) return;
      if (anchorRef.current?.contains(t)) return;
      onClose();
    };
    const onScroll = () => onClose();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onClose);
    };
  }, [open, anchorRef, onClose]);

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={popRef}
      style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 10000 }}
      onClick={(e) => e.stopPropagation()}
    >
      <Picker data={data} onEmojiSelect={onEmojiSelect} emojiSize={22} emojiButtonSize={34} perLine={8} previewPosition="none" />
    </div>,
    document.body
  );
};

export default EmojiPopover;
